<script setup>
import { computed, ref, watchEffect, onMounted } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import { useDesktopIconsStore } from '@/stores/desktopIconsStore'

const emit = defineEmits()

const props = defineProps({
  entities: {
    type: Array,
    required: true
  }
})

const localeStore = useLocaleStore()
const desktopIconsStore = useDesktopIconsStore()

const localEntities = ref([])
const draggedIcon = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const clickStartPos = ref({ x: 0, y: 0 })
const DRAG_THRESHOLD = 5 // Distance en pixels pour activer le drag

watchEffect(() => {
  localEntities.value = [...props.entities]
})

// Charger les positions sauvegardées au montage
onMounted(() => {
  desktopIconsStore.loadState()
})

// Configuration de la grille par défaut
const GRID_COLUMNS = 2
const GRID_GAP = 80 // Gap horizontal entre icônes
const ROW_GAP = 100 // Gap vertical entre icônes
const INITIAL_X = 40
const INITIAL_Y = 40
const ICON_WIDTH = 100

// Trier par ordre alphabétique et charger les positions
const desktopEntities = computed(() => {
  // Filtrer et trier d'abord
  const sortedEntities = localEntities.value
    .filter((entity) => entity.onDesktop)
    .map((entity) => {
      const title = entity.title[localeStore.currentLocale] || entity.title['fr']
      return {
        ...entity,
        titleText: title
      }
    })
    .sort((a, b) => {
      // Trier par ordre alphabétique
      return a.titleText.localeCompare(b.titleText, localeStore.currentLocale === 'fr' ? 'fr' : 'en')
    })

  // Ensuite, ajouter les positions
  return sortedEntities.map((entity, index) => {
    const savedPosition = desktopIconsStore.getIconPosition(entity.id)

    // Position par défaut en grille si pas de position sauvegardée
    let defaultPosition = null
    if (!savedPosition) {
      const row = Math.floor(index / GRID_COLUMNS)
      const col = index % GRID_COLUMNS
      defaultPosition = {
        x: INITIAL_X + col * (ICON_WIDTH + GRID_GAP),
        y: INITIAL_Y + row * ROW_GAP
      }
    }

    return {
      ...entity,
      position: savedPosition || defaultPosition
    }
  })
})

const toggleEffect = (selectedEntity) => {
  localEntities.value.forEach((entity) => {
    entity.isActive = entity.id === selectedEntity.id
  })
}

const removeFilterAndToggle = (entity) => {
  localEntities.value.forEach((e) => {
    e.isActive = false
  })
  emit('toggle-' + entity.id)
}

// Detect if the user is on a mobile device
const isMobile = ref(false)

if (typeof window !== 'undefined') {
  isMobile.value = /Mobi|Android/i.test(navigator.userAgent)
}

// Computed property to get the localized title
const getLocalizedTitle = (entity) => {
  return entity.title[localeStore.currentLocale] || entity.title['fr']
}

// Drag & Drop functionality
const startDrag = (event, entity) => {
  // Empêcher le drag sur mobile
  if (isMobile.value) return

  // Empêcher le drag si c'est un simple clic
  if (event.button !== 0) return

  clickStartPos.value = { x: event.clientX, y: event.clientY }
  draggedIcon.value = entity
  isDragging.value = false

  const rect = event.currentTarget.getBoundingClientRect()
  const savedPosition = desktopIconsStore.getIconPosition(entity.id)

  dragOffset.value = {
    x: event.clientX - (savedPosition ? savedPosition.x : rect.left),
    y: event.clientY - (savedPosition ? savedPosition.y : rect.top)
  }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

const onDrag = (event) => {
  if (!draggedIcon.value) return

  const deltaX = Math.abs(event.clientX - clickStartPos.value.x)
  const deltaY = Math.abs(event.clientY - clickStartPos.value.y)

  // Activer le drag seulement si le seuil est dépassé
  if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
    isDragging.value = true
    event.preventDefault()

    const newX = event.clientX - dragOffset.value.x
    const newY = event.clientY - dragOffset.value.y

    // Limiter la position dans les limites du viewport
    const maxX = window.innerWidth - 100
    const maxY = window.innerHeight - 100

    const clampedX = Math.max(0, Math.min(newX, maxX))
    const clampedY = Math.max(0, Math.min(newY, maxY))

    // Sauvegarder la position
    desktopIconsStore.saveIconPosition(draggedIcon.value.id, {
      x: clampedX,
      y: clampedY
    })
  }
}

const stopDrag = (event) => {
  const wasDragging = isDragging.value

  if (wasDragging) {
    // Si on était en train de drag, empêcher le clic
    event?.preventDefault()
    event?.stopPropagation()
  }

  // Réinitialiser après un court délai pour éviter les clics
  setTimeout(
    () => {
      isDragging.value = false
      draggedIcon.value = null
    },
    wasDragging ? 100 : 0
  )

  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

// Si click survient ailleurs, retirer l'état actif
window.addEventListener('click', (e) => {
  if (!e.target.closest('.desktop-icon-container')) {
    localEntities.value.forEach((entity) => {
      entity.isActive = false
    })
  }
})
</script>

<template>
  <section class="absolute top-0 left-0 w-full h-full pointer-events-none">
    <div class="relative w-full h-full">
      <div
        v-for="entity in desktopEntities"
        :key="entity.id"
        class="desktop-icon-container absolute flex flex-col gap-2 items-center cursor-pointer transition-all duration-300 pointer-events-auto"
        :class="{ active: entity.isActive, 'opacity-90': draggedIcon?.id === entity.id }"
        :style="{
          left: entity.position.x !== null ? `${entity.position.x}px` : 'auto',
          top: entity.position.y !== null ? `${entity.position.y}px` : 'auto',
          transform: draggedIcon?.id === entity.id ? 'scale(1.1)' : 'scale(1)',
          zIndex: draggedIcon?.id === entity.id ? 1000 : 'auto'
        }"
        @mousedown="startDrag($event, entity)"
        @click="!isDragging && (isMobile ? removeFilterAndToggle(entity) : toggleEffect(entity))"
        @dblclick="!isMobile && !isDragging && removeFilterAndToggle(entity)"
        :title="getLocalizedTitle(entity)"
      >
        <img
          class="w-11 h-11 desktop-icon transition-all duration-300 select-none"
          :style="{
            ...entity.imageStyle,
            filter: entity.isActive ? 'opacity(0.5) drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'opacity(1) drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
            pointerEvents: 'none'
          }"
          :src="entity.imgSrc"
          :alt="getLocalizedTitle(entity)"
          draggable="false"
        />
        <p
          class="text-white text-xs font-normal py-px px-1 text-center select-none pointer-events-none"
          :style="{
            ...entity.textStyle,
            backgroundColor: entity.isActive ? '#0B61FF' : 'transparent',
            textShadow: entity.isActive ? 'none' : '0px 1px 1px rgba(1, 1, 1, 1), 0px 0px 4px #000',
            maxWidth: '100px'
          }"
        >
          {{ getLocalizedTitle(entity) }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.desktop-icon-container {
  width: 100px;
  min-height: 80px;
  user-select: none;
}

.desktop-icon-container:active {
  cursor: grabbing;
}

.desktop-icon-container:not(:active) {
  cursor: grab;
}

.desktop-icon-container.active {
  opacity: 0.9;
}
</style>
